// Wraps up the the functionality and the UI around the dropzone we use with richdown composer over MessagesApp.jsx.
// Probably will have to be more generalized, or at least renamed, when we want to use it in the agent chat too.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDropzone } from 'react-dropzone';

const noop = () => {};
const DEFAULT_DROPZONE_CONFIG = {
  disabled: false,
  onDrop: noop,
  onDropRejected: noop,
};

const useComposerDropzone = ({ isInteractive }) => {
  const [dropzoneConfig, setDropzoneConfig] = useState(DEFAULT_DROPZONE_CONFIG);
  // Keeps the previous message visible just long enough for the UI to read once the drag flags reset.
  const [frozenMessageType, setFrozenMessageType] = useState(null);
  const frozenMessageTimeoutRef = useRef(null);

  const dropzoneGloballyDisabled = !isInteractive || dropzoneConfig.disabled;

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    open: openFileDialog,
    rootRef,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: dropzoneGloballyDisabled,
    onDrop: dropzoneConfig.onDrop,
    onDropRejected: dropzoneConfig.onDropRejected,
    preventDropOnDocument: isInteractive,
  });

  const dropzoneHelpers = useMemo(() => {
    if (!isInteractive) return null;
    return {
      getInputProps,
      open: openFileDialog,
      isDragActive,
      isDragReject,
      disabled: dropzoneConfig.disabled,
      rootRef,
    };
  }, [isInteractive, getInputProps, openFileDialog, isDragActive, isDragReject, dropzoneConfig.disabled, rootRef]);

  const handleDropzoneStateChange = useCallback(
    (config) => {
      if (!isInteractive) {
        setDropzoneConfig(DEFAULT_DROPZONE_CONFIG);
        return;
      }

      setDropzoneConfig((prev) => {
        const next = config ?? DEFAULT_DROPZONE_CONFIG;
        if (prev.disabled === next.disabled && prev.onDrop === next.onDrop && prev.onDropRejected === next.onDropRejected) {
          return prev;
        }
        return next;
      });
    },
    [isInteractive],
  );

  // Keep callback for backward compatibility, but it's no longer used internally
  const handleUploadCountChange = useCallback(() => {}, []);

  useEffect(() => {
    return () => {
      if (frozenMessageTimeoutRef.current) {
        clearTimeout(frozenMessageTimeoutRef.current);
        frozenMessageTimeoutRef.current = null;
      }
    };
  }, []);

  // React-dropzone clears drag flags which can briefly blank the overlay.
  // Snapshot the last seen message and hold it until we have a replacement so the user never sees a flicker.
  const dragMessage = useMemo(() => {
    if (!isDragActive) return null;
    return 'normal';
  }, [isDragActive]);

  useEffect(() => {
    if (!dragMessage) return;

    setFrozenMessageType(dragMessage);
    if (frozenMessageTimeoutRef.current) {
      clearTimeout(frozenMessageTimeoutRef.current);
      frozenMessageTimeoutRef.current = null;
    }
  }, [dragMessage]);

  useEffect(() => {
    if (dragMessage) return;
    if (frozenMessageType === null) return;

    if (frozenMessageTimeoutRef.current) {
      clearTimeout(frozenMessageTimeoutRef.current);
    }

    // Allow one more animation frame with the last message so the overlay doesn't flicker
    frozenMessageTimeoutRef.current = setTimeout(() => {
      setFrozenMessageType(null);
      frozenMessageTimeoutRef.current = null;
    }, 50);

    return () => {
      if (frozenMessageTimeoutRef.current) {
        clearTimeout(frozenMessageTimeoutRef.current);
        frozenMessageTimeoutRef.current = null;
      }
    };
  }, [dragMessage, frozenMessageType]);

  const currentDropzoneMessage = useMemo(() => {
    return dragMessage ?? frozenMessageType;
  }, [dragMessage, frozenMessageType]);

  return {
    dropzoneConfig,
    dropzoneHelpers,
    getRootProps,
    handleDropzoneStateChange,
    handleUploadCountChange,
    currentDropzoneMessage,
    showDropzoneOverlay: !!currentDropzoneMessage,
  };
};

export default useComposerDropzone;
